import {
  validateAmountNonNegative,
  validateAmountPositive,
  validatePhoneNumber,
} from "@/utils/validation";
import { alphanumericAccountNumberValidationMessage } from "@/utils/sensitiveAccountNumber";
import { validatePanNumber } from "@/utils/panValidation";
import { placeholderNameFieldError } from "@/utils/familyProfileUtils";
import { getProductionAssetNameError } from "@/utils/productionUserContentValidation";
import { validateInvitableEmailSecurity } from "../../shared-utils/emailSignupSecurity.mjs";
import { isValidGoalDeadline } from "@/utils/goals/goalDeadline";

export type FieldErrors = Record<string, string>;

function applyProductionUserContentLabelError(
  errors: FieldErrors,
  fieldKey: string,
  label: string
): void {
  const productionError = getProductionAssetNameError(label);
  if (productionError) {
    errors[fieldKey] = productionError;
  }
}

/**
 * Validate Add Transaction Fields.
 */
export function validateAddTransactionFields(input: {
  description: string;
  category: string;
  amount: string;
  date: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.description.trim()) {
    errors.description = "Description is required";
  } else {
    applyProductionUserContentLabelError(errors, "description", input.description);
  }
  if (!input.category.trim()) {
    errors.category = "Category is required";
  }
  if (!input.date.trim()) {
    errors.date = "Date is required";
  }

  const amountCheck = validateAmountPositive(input.amount, "Amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  return errors;
}

/**
 * Validate Add Goal Fields.
 */
export function validateAddGoalFields(input: {
  name: string;
  target: string;
  current: string;
  goalType: "individual" | "family";
  selectedUserId: string;
  deadline?: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.name.trim()) {
    errors.name = "Goal name is required";
  } else {
    applyProductionUserContentLabelError(errors, "name", input.name);
  }

  const targetCheck = validateAmountPositive(input.target, "Target amount");
  if (!targetCheck.valid && targetCheck.error) {
    errors.target = targetCheck.error;
  }

  const currentCheck = validateAmountNonNegative(input.current || "0", "Current amount");
  if (!currentCheck.valid && currentCheck.error) {
    errors.current = currentCheck.error;
  }

  if (input.goalType === "individual" && !input.selectedUserId) {
    errors.selectedUserId = "Select a goal owner for individual goals";
  }

  if (input.deadline?.trim()) {
    if (!isValidGoalDeadline(input.deadline)) {
      errors.deadline = "Enter a valid date (YYYY-MM-DD) between 1970 and 2100";
    }
  }

  return errors;
}

/**
 * Validate Edit Goal Fields.
 */
export function validateEditGoalFields(input: {
  name: string;
  target: string;
  current: string;
  deadline: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.name.trim()) {
    errors.name = "Goal name is required";
  } else {
    applyProductionUserContentLabelError(errors, "name", input.name);
  }

  const targetCheck = validateAmountPositive(input.target, "Target amount");
  if (!targetCheck.valid && targetCheck.error) {
    errors.target = targetCheck.error;
  }

  const currentCheck = validateAmountNonNegative(input.current || "0", "Current amount");
  if (!currentCheck.valid && currentCheck.error) {
    errors.current = currentCheck.error;
  }

  if (!input.deadline.trim()) {
    errors.deadline = "Deadline is required";
  } else if (!isValidGoalDeadline(input.deadline)) {
    errors.deadline = "Enter a valid date (YYYY-MM-DD) between 1970 and 2100";
  }

  return errors;
}

/**
 * Validate PFPpf VPFFields.
 */
export function validatePFPpfVPFFields(input: {
  name: string;
  accountNo: string;
  amount: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.name.trim()) {
    errors.name = "Name is required";
  } else {
    applyProductionUserContentLabelError(errors, "name", input.name);
  }

  const accountError = alphanumericAccountNumberValidationMessage(input.accountNo);
  if (accountError) {
    errors.accountNo = accountError;
  }

  const amountCheck = validateAmountPositive(input.amount, "Amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  return errors;
}

/**
 * Validate Insurance Fields.
 */
export function validateInsuranceFields(input: {
  name: string;
  insurer: string;
  sumAssured: string;
  premium: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.name.trim()) {
    errors.name = "Policy name is required";
  } else {
    applyProductionUserContentLabelError(errors, "name", input.name);
  }
  if (!input.insurer.trim()) {
    errors.insurer = "Insurer is required";
  }

  const sumCheck = validateAmountPositive(input.sumAssured, "Sum assured");
  if (!sumCheck.valid && sumCheck.error) {
    errors.sumAssured = sumCheck.error;
  }

  const premiumCheck = validateAmountPositive(input.premium, "Premium");
  if (!premiumCheck.valid && premiumCheck.error) {
    errors.premium = premiumCheck.error;
  }

  return errors;
}

/**
 * Validate Gold Fields.
 */
export function validateGoldFields(input: {
  name: string;
  type: string;
  amount: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.name.trim()) {
    errors.name = "Asset name is required";
  } else {
    applyProductionUserContentLabelError(errors, "name", input.name);
  }
  if (!input.type.trim()) {
    errors.type = "Sub-type is required";
  }

  const amountCheck = validateAmountPositive(input.amount, "Amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  return errors;
}

/**
 * Validate Employer Pf Asset Fields.
 */
export function validateEmployerPfAssetFields(input: {
  employer_name: string;
  account_number: string;
  current_balance: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.employer_name.trim()) {
    errors.employer_name = "Employer name is required";
  } else {
    applyProductionUserContentLabelError(errors, "employer_name", input.employer_name);
  }

  const accountError = alphanumericAccountNumberValidationMessage(input.account_number);
  if (accountError) {
    errors.account_number = accountError;
  }

  const balanceCheck = validateAmountPositive(input.current_balance, "Current balance");
  if (!balanceCheck.valid && balanceCheck.error) {
    errors.current_balance = balanceCheck.error;
  }

  return errors;
}

/**
 * Validate Ppf Asset Fields.
 */
export function validatePpfAssetFields(input: {
  bank_name: string;
  account_number: string;
  current_balance: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.bank_name.trim()) {
    errors.bank_name = "Bank name is required";
  } else {
    applyProductionUserContentLabelError(errors, "bank_name", input.bank_name);
  }

  const accountError = alphanumericAccountNumberValidationMessage(input.account_number);
  if (accountError) {
    errors.account_number = accountError;
  }

  const balanceCheck = validateAmountPositive(input.current_balance, "Current balance");
  if (!balanceCheck.valid && balanceCheck.error) {
    errors.current_balance = balanceCheck.error;
  }

  return errors;
}

/**
 * Validate Split Mage Add Expense Fields.
 */
export function validateSplitMageAddExpenseFields(input: {
  description: string;
  amount: string;
  paidBy: string;
  membersCount: number;
  splitType: "equal" | "custom";
  customSplits: Record<string, string>;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.description.trim()) {
    errors.description = "Description is required";
  }

  const amountCheck = validateAmountPositive(input.amount, "Amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  if (input.membersCount > 0 && !input.paidBy) {
    errors.paidBy = "Select who paid for this expense";
  }

  if (input.splitType === "custom") {
    const amountNum = Number.parseFloat(input.amount.replace(/,/g, ""));
    if (!Number.isNaN(amountNum)) {
      const totalCustom = Object.values(input.customSplits).reduce(
        (sum, val) => sum + (Number.parseFloat(val) || 0),
        0
      );
      if (Math.abs(totalCustom - amountNum) > 0.01) {
        errors.customSplits = `Custom splits must equal ${amountNum.toFixed(2)}`;
      }
    }
  }

  return errors;
}

/**
 * Validate Edit Split Mage Expense Fields.
 */
export function validateEditSplitMageExpenseFields(input: {
  description: string;
  amount: string;
  expenseDate: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.description.trim()) {
    errors.description = "Description is required";
  }

  const amountCheck = validateAmountPositive(input.amount, "Amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  if (!input.expenseDate.trim()) {
    errors.expenseDate = "Date is required";
  }

  return errors;
}

/**
 * Validate Create Group Fields.
 */
export function validateCreateGroupFields(input: {
  title: string;
  description?: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.title.trim()) {
    errors.title = "Group name is required";
  } else if (input.title.trim().length > 30) {
    errors.title = "Group name must be 30 characters or less";
  }

  if (input.description && input.description.length > 200) {
    errors.description = "Description must be 200 characters or less";
  }

  return errors;
}

/**
 * Is Valid Optional Website.
 */
function isValidOptionalWebsite(website: string): boolean {
  const trimmed = website.trim();
  if (!trimmed) {
    return true;
  }
  try {
    const withProtocol = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    const url = new URL(withProtocol);
    return Boolean(url.hostname.includes("."));
  } catch {
    return false;
  }
}

/**
 * Validate Profile Fields.
 */
export function validateProfileFields(input: {
  fullName: string;
  website?: string;
  phone?: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.fullName.trim()) {
    errors.fullName = "Full name is required";
  }

  if (input.website && !isValidOptionalWebsite(input.website)) {
    errors.website = "Enter a valid website URL";
  }

  if (input.phone?.trim()) {
    const phoneError = validatePhoneNumber(input.phone);
    if (phoneError) {
      errors.phone = phoneError;
    }
  }

  return errors;
}

/**
 * Validate Quick Action Transaction Fields.
 */
export function validateQuickActionTransactionFields(input: {
  amount: string;
  category: string;
  date?: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!input.category.trim()) {
    errors.category = "Please select a category.";
  }
  if (!input.date?.trim()) {
    errors.date = "Please select a date.";
  }

  const amountCheck = validateAmountPositive(input.amount, "Transaction amount");
  if (!amountCheck.valid && amountCheck.error) {
    errors.amount = amountCheck.error;
  }

  return errors;
}

export function validateQuickActionInvestmentFields(input: { category: string }): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.category.trim()) {
    errors.category = "Choose an investment type from the list to continue.";
  }
  return errors;
}

/**
 * Validate Quick Action Goal Fields.
 */
export function validateQuickActionGoalFields(input: {
  name: string;
  target_amount: string;
  current_amount?: string;
  goal_type?: "individual" | "family";
  individual_user_id?: string;
}): FieldErrors {
  const baseErrors = validateAddGoalFields({
    name: input.name,
    target: input.target_amount,
    current: input.current_amount || "0",
    goalType: input.goal_type || "family",
    selectedUserId: input.individual_user_id || "",
  });

  const errors: FieldErrors = {};
  if (baseErrors.name) {
    errors.name = baseErrors.name;
  }
  if (baseErrors.target) {
    errors.target_amount = baseErrors.target;
  }
  if (baseErrors.current) {
    errors.current_amount = baseErrors.current;
  }
  if (baseErrors.selectedUserId) {
    errors.individual_user_id = baseErrors.selectedUserId;
  }
  return errors;
}

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validate Email Address.
 */
export function validateEmailAddress(email: string, fieldName = "Email"): FieldErrors {
  const errors: FieldErrors = {};
  const trimmed = email.trim();
  if (!trimmed) {
    errors.email = `${fieldName} is required`;
    return errors;
  }
  if (!EMAIL_PATTERN.test(trimmed)) {
    errors.email = `Enter a valid ${fieldName.toLowerCase()} address`;
  }
  return errors;
}

/** Format + signup-equivalent blocklist for any platform invite (family, company, etc.). */
export function validateInviteEmailAddress(
  email: string,
  fieldName = "Email",
  fieldKey = "email"
): FieldErrors {
  const formatErrors = validateEmailAddress(email, fieldName);
  if (formatErrors.email) {
    return fieldKey === "email" ? formatErrors : { [fieldKey]: formatErrors.email };
  }

  const security = validateInvitableEmailSecurity(email.trim());
  if (!security.allowed) {
    return {
      [fieldKey]: security.reason || `${fieldName} is not allowed for invitations`,
    };
  }

  return {};
}

export function validateBulkImportInviteRows(
  rows: Array<{ email: string }>
): { rowIndex: number; error: string } | null {
  for (let index = 0; index < rows.length; index += 1) {
    const emailErrors = validateInviteEmailAddress(rows[index].email, "Email");
    if (emailErrors.email) {
      return { rowIndex: index, error: emailErrors.email };
    }
  }
  return null;
}

/**
 * Validate Optional Email Address.
 */
export function validateOptionalEmailAddress(
  email: string,
  fieldName = "Email",
  fieldKey = "email"
): FieldErrors {
  const trimmed = email.trim();
  if (!trimmed) {
    return {};
  }
  if (!EMAIL_PATTERN.test(trimmed)) {
    return { [fieldKey]: `Enter a valid ${fieldName.toLowerCase()} address` };
  }
  return {};
}

export function validateMfaVerificationCode(code: string): FieldErrors {
  const errors: FieldErrors = {};
  if (code.length !== 6) {
    errors.verificationCode = "Enter a 6-digit verification code";
    return errors;
  }
  if (!/^\d{6}$/.test(code)) {
    errors.verificationCode = "Code must be 6 digits";
  }
  return errors;
}

/**
 * Validate Create Family Fields.
 */
export function validateCreateFamilyFields(input: {
  familyName: string;
  fullName: string;
  panNumber: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  const familyNameError = placeholderNameFieldError(input.familyName, "Family name");
  if (familyNameError) {
    errors.familyName = familyNameError;
  }

  const fullNameError = placeholderNameFieldError(input.fullName, "Full name");
  if (fullNameError) {
    errors.fullName = fullNameError;
  }

  const panResult = validatePanNumber(input.panNumber);
  if (!panResult.valid && panResult.error) {
    errors.panNumber = panResult.error;
  }

  return errors;
}

/**
 * Validate Family Goal Fields.
 */
export function validateFamilyGoalFields(input: {
  goalName: string;
  targetAmount: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.goalName.trim()) {
    errors.goalName = "Goal name is required";
  } else {
    applyProductionUserContentLabelError(errors, "goalName", input.goalName);
  }
  const targetCheck = validateAmountPositive(input.targetAmount, "Target amount");
  if (!targetCheck.valid && targetCheck.error) {
    errors.targetAmount = targetCheck.error;
  }
  return errors;
}

/**
 * Validate Contact Form Fields.
 */
export function validateContactFormFields(input: {
  name: string;
  email: string;
  message: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.name.trim()) {
    errors.name = "Name is required";
  }
  const emailErrors = validateEmailAddress(input.email, "Email");
  if (emailErrors.email) {
    errors.email = emailErrors.email;
  }
  if (!input.message.trim()) {
    errors.message = "Message is required";
  } else if (input.message.trim().length < 10) {
    errors.message = "Please provide at least 10 characters in your message";
  }
  return errors;
}

const ASSET_TYPE_MIN_LENGTH = 3;
const ASSET_IMPORTANCE_MIN_LENGTH = 10;

/**
 * Validate Asset Request Fields.
 */
export function validateAssetRequestFields(input: {
  assetType: string;
  importance: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  if (input.assetType.trim().length < ASSET_TYPE_MIN_LENGTH) {
    errors.assetType = "Please provide at least 3 characters for asset type";
  }
  if (input.importance.trim().length < ASSET_IMPORTANCE_MIN_LENGTH) {
    errors.importance = "Please provide at least 10 characters explaining importance";
  }
  return errors;
}

/**
 * Validate Webhook Url.
 */
function validateWebhookUrl(url: string): string | undefined {
  const trimmed = url.trim();
  if (!trimmed) {
    return "Webhook URL is required";
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return "Enter a valid http or https URL";
    }
  } catch {
    return "Enter a valid webhook URL";
  }
  return undefined;
}

/**
 * Validate Webhook Create Fields.
 */
export function validateWebhookCreateFields(input: {
  url: string;
  secret: string;
  eventsCount: number;
}): FieldErrors {
  const errors: FieldErrors = {};
  const urlError = validateWebhookUrl(input.url);
  if (urlError) {
    errors.url = urlError;
  }
  if (!input.secret.trim()) {
    errors.secret = "Webhook secret is required";
  } else if (input.secret.trim().length < 16) {
    errors.secret = "Secret must be at least 16 characters";
  }
  if (input.eventsCount === 0) {
    errors.events = "Select at least one event";
  }
  return errors;
}

export function validateWebhookUpdateFields(input: { url: string; secret: string }): FieldErrors {
  const errors: FieldErrors = {};
  const urlError = validateWebhookUrl(input.url);
  if (urlError) {
    errors.url = urlError;
  }
  if (input.secret.trim() && input.secret.trim().length < 16) {
    errors.secret = "Secret must be at least 16 characters";
  }
  return errors;
}

/**
 * Validate Update Password Fields.
 */
export function validateUpdatePasswordFields(input: {
  newPassword: string;
  confirmPassword: string;
  isPasswordValid: boolean;
}): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.newPassword) {
    errors.newPassword = "New password is required";
  } else if (!input.isPasswordValid) {
    errors.newPassword = "Password does not meet all requirements";
  }
  if (!input.confirmPassword) {
    errors.confirmPassword = "Please confirm your new password";
  } else if (input.newPassword !== input.confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }
  return errors;
}
